import { useState, useEffect, useCallback } from 'react'
import { getSseReleases, getSseWorkstations, registerSseRelease, setSseRollout, getLicenseProducts } from '../services/api'

/**
 * Product Updates — release registry + fleet default, as a collapsible panel on the
 * Licenses page.
 *
 * 🔴 PER PRODUCT. This was SSE-only, and once a second product registered a release its rows
 * appeared in this table with nothing to say which product they belonged to — and the fleet
 * default it set would have been read as SSE's. Every call now carries a slug.
 *
 * This used to be its own tab, with a workstation table beside these two controls.
 * That table duplicated the Licenses table: the workstation row is created BY licence
 * validation (licenseService upserts SseWorkstation from the same heartbeat that stamps
 * License.lastCheckedAt), so device code, customer name and last-seen appeared twice and
 * an operator ended up retyping a name the licence already held. Per-machine state now
 * lives on the licence row itself; what remains here is the part that is genuinely about
 * BUILDS rather than machines.
 *
 * Collapsed by default — registering a release is rare, the licence list is the daily
 * view — with the current default version in the header so the fleet setting is visible
 * without expanding.
 *
 * @param {() => void} onChange called after a release or default changes, so the
 *        licence list can refresh its Update column.
 */
export default function SseUpdatesPanel({ onChange }) {
  const [open, setOpen] = useState(false)
  const [product, setProduct] = useState('sse')
  const [products, setProducts] = useState([])
  const [releases, setReleases] = useState([])
  const [defaultVersion, setDefaultVersion] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(null) // 'rollout' | 'release'
  const [form, setForm] = useState({ platform: 'windows-x86_64', version: '', driveFileId: '', signature: '', notes: '' })

  const load = useCallback(async () => {
    setErr(null)
    try {
      // The fleet default is served by the workstations endpoint; read it from there so
      // there is exactly one source for it, rather than a second copy that can drift.
      const [r, w] = await Promise.all([getSseReleases(product), getSseWorkstations(product)])
      setReleases(r.data.releases || [])
      setDefaultVersion(w.data.defaultVersion || '')
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load SSE release data.')
    } finally {
      setLoading(false)
    }
  }, [product])
  useEffect(() => { load() }, [load])

  // The product list drives the selector. Failing to load it must not break the panel —
  // it falls back to SSE, which is what every existing release belongs to.
  useEffect(() => {
    getLicenseProducts()
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
  }, [])

  // Numeric compare, newest first: a plain lexicographic sort puts 0.16.9 above
  // 0.16.16, which is wrong for versions that already exist.
  const versions = [...new Set(releases.map((r) => r.version))].sort((a, b) => {
    const pa = String(a).split('.').map(Number), pb = String(b).split('.').map(Number)
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pb[i] || 0) - (pa[i] || 0)
      if (d) return d
    }
    return 0
  })

  const submitRelease = async (e) => {
    e.preventDefault()
    setSaving('release'); setErr(null)
    try {
      await registerSseRelease(form, product)
      setForm((f) => ({ ...f, version: '', driveFileId: '', signature: '', notes: '' }))
      await load(); onChange?.()
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to register release.')
    } finally { setSaving(null) }
  }

  const saveRollout = async () => {
    setSaving('rollout'); setErr(null)
    try { await setSseRollout(defaultVersion || null, product); await load(); onChange?.() }
    catch (e2) { setErr(e2.response?.data?.error || 'Failed to set rollout default.') }
    finally { setSaving(null) }
  }

  return (
    <section className="bg-white rounded-xl border border-gray-200 mb-4">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 rounded-xl">
        <span className={`text-gray-400 text-xs transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        <span className="text-sm font-semibold text-gray-800">Updates</span>
        <span className="text-xs font-medium text-brand-700">{product}</span>
        {/* An error must NOT read as "updates paused" — that is a deliberate fleet
            state, and reporting it when the backend is simply unreachable would tell
            an admin the opposite of the truth. */}
        <span className={`text-xs ${err ? 'text-red-600' : 'text-gray-500'}`}>
          {loading ? 'loading…' : err ? 'unavailable' : defaultVersion ? `default ${defaultVersion}` : 'updates paused'}
        </span>
        <span className="ml-auto text-xs text-gray-400">
          {err ? '' : `${releases.length} release${releases.length === 1 ? '' : 's'}`}
        </span>
      </button>
      {err && !open && <div className="px-4 pb-3 -mt-1 text-xs text-red-600">{err}</div>}

      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-5">
          {err && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{err}</div>}

          {/* ── Which product ── */}
          {/* First, because everything below is scoped to it: a version registered against
              the wrong product is offered to the wrong fleet. */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Product</label>
            <select value={product} onChange={(e) => setProduct(e.target.value)}
              className="border rounded-lg px-2 py-1 text-sm">
              {(products.length ? products : [{ slug: 'sse', name: 'SurgicalSuiteEngine' }])
                .map((p) => <option key={p.slug} value={p.slug}>{p.name} ({p.slug})</option>)}
            </select>
          </div>

          {/* ── Fleet default ── */}
          <div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Default version</label>
              <select value={defaultVersion} onChange={(e) => setDefaultVersion(e.target.value)}
                className="border rounded-lg px-2 py-1 text-sm">
                <option value="">(none — updates paused)</option>
                {versions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <button onClick={saveRollout} disabled={saving === 'rollout'}
                className="px-3 py-1 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 disabled:opacity-60">
                {saving === 'rollout' ? 'Saving…' : 'Save'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Every workstation follows this unless its licence row sets a different target.
              The app only ever prompts — it never installs without the operator clicking Update.
            </p>
          </div>

          {/* ── Register a release ── */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Register a release</h3>
            <form onSubmit={submitRelease} className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-xs">
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="border rounded-lg px-2 py-1.5">
                <option value="windows-x86_64">Windows x64</option>
                <option value="darwin-aarch64">macOS (Apple Silicon)</option>
                <option value="darwin-x86_64">macOS (Intel)</option>
              </select>
              <input required placeholder="Version (0.16.17)" value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })} className="border rounded-lg px-2 py-1.5" />
              <input required placeholder="Drive file ID (updater artifact)" value={form.driveFileId}
                onChange={(e) => setForm({ ...form, driveFileId: e.target.value })} className="border rounded-lg px-2 py-1.5" />
              <input required placeholder="Signature (.sig contents)" value={form.signature}
                onChange={(e) => setForm({ ...form, signature: e.target.value })} className="border rounded-lg px-2 py-1.5" />
              <div className="flex gap-2">
                <input placeholder="Notes" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border rounded-lg px-2 py-1.5 flex-1" />
                <button type="submit" disabled={saving === 'release'}
                  className="px-3 py-1.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-60">
                  {saving === 'release' ? '…' : 'Register'}
                </button>
              </div>
            </form>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1.5 pr-2">Version</th><th className="py-1.5 pr-2">Platform</th>
                  <th className="py-1.5 pr-2">Published</th><th className="py-1.5">Notes</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 font-medium">{r.version}</td>
                    <td className="py-1.5 pr-2">{r.platform}</td>
                    <td className="py-1.5 pr-2 text-gray-500">{new Date(r.pubDate).toLocaleString()}</td>
                    <td className="py-1.5 text-gray-500">{r.notes || '—'}</td>
                  </tr>
                ))}
                {!releases.length && (
                  <tr><td colSpan={4} className="py-3 text-gray-400 text-center">
                    No releases registered. Build with signing, upload the updater artifact to Drive,
                    then register it here (scripts/publish-sse-update.sh automates this).
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}
